import { useMutation, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '../../../config/queryKeys'
import {
  AdditionalInvoiceBody,
  addAdditionalInvoice,
  autoDistribute,
  BalanceEmailBody,
  BalanceLineKind,
  broadcastBalanceEmail,
  createBalanceOfficialLine,
  createBalanceSlots,
  deleteBalanceSlot,
  lockBalanceSlot,
  lockBatch,
  OfflinePaymentBody,
  postAndNotifyTeam,
  recordOfflinePayment,
  releaseBalanceSlot,
  removeAdditionalInvoice,
  sendPaymentReminders,
  sendSlotReminder,
  SlotFinderAction,
  unlockBalanceSlot,
  updateBalanceSettings,
  updateBalanceSlot,
  updateSlotFinderListing,
  updateSlotNote,
  verifyOfflinePayment,
  voidOfflinePayment,
} from '../../../services/teamBalance'

/** List (with the rep's intent and position) or remove one slot's listing. */
export type SlotFinderListingVariables =
  | {
      slotId: string
      listed: true
      action: SlotFinderAction
      playerTypeId?: string | null
    }
  | { slotId: string; listed: false }

/**
 * SN-4709 balance mutations. Invalidate, never optimistic — the server owns
 * penny-exact re-splits, KPIs and derived statuses (D20). Errors propagate with
 * req()'s status / invalid_fields metadata intact for the caller to surface.
 *
 * retry: 0 on every mutation, deliberately overriding the app-wide
 * mutations.retry: 1 — none of these calls are idempotent (POST slots inserts
 * count rows per attempt), and the service layer already disables transport
 * retries for the same reason.
 */
export const useTeamBalanceMutations = (teamId: string, rosterId: string) => {
  'use memo'
  const queryClient = useQueryClient()

  // Invalidate the WHOLE teamBalance tree, not just this roster's sheet:
  // discovery (which rosters carry balance) can change as a side effect of these
  // mutations — the last slot on a roster being deleted, or a registration
  // becoming/ceasing to be distribution-enabled — and a stale discovery list
  // leaves the switcher offering a roster whose sheet no longer exists.
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.teamBalance.all })

  // SN-4709 × SN-8081 — the Team page's Player Finder settings read the listing
  // these slots project into, so creating, listing, unlisting, deleting or
  // releasing a slot can change what that sub-tab shows (recruiting, positions,
  // spots left). Money-only mutations cannot, so they keep the balance-only
  // invalidation rather than refetching the settings probe on every edit.
  const invalidateWithPlayerFinder = () =>
    Promise.all([
      invalidate(),
      queryClient.invalidateQueries({
        queryKey: queryKeys.teamPlayerFinder.all,
      }),
    ])

  // Review round 1 (TF-01/TF-02) — a 409 means the sheet the rep acted on is
  // stale: its registration moved to another division, or the slot changed,
  // since it loaded. Nothing was written; refetch so the next action is chosen
  // from what is true now. The offline payment mutations keep their own 409
  // protocols (stale preview, drifted verification), driven from their modals.
  const refetchOnConflict = (error: unknown) =>
    (error as { status?: number } | null)?.status === 409
      ? invalidateWithPlayerFinder()
      : undefined

  // Review round 1 (F3) — a write confirmed against a stale preview is refused with a 422
  // (`preview_changed`, or `preview` on the SN-8310 add) and writes nothing — but it means the
  // sheet the rep previewed has moved since: the fee, a share or an occupant changed. Refetch it
  // as after a 409, so the numbers behind the re-shown preview are current too, even if the rep
  // then cancels. Only the fingerprinted writes use this.
  const isStalePreview = (error: unknown) => {
    const fields = (
      error as { invalid_fields?: Record<string, unknown> } | null
    )?.invalid_fields
    return Boolean(fields?.preview_changed || fields?.preview)
  }

  const refetchWhenStale = (error: unknown) =>
    isStalePreview(error) ? invalidate() : refetchOnConflict(error)

  const createSlots = useMutation({
    mutationFn: (body: {
      team_registration_id: string
      count: number
      // D46 — the Add Player Slot modal's Player Finder opt-in. Optional and
      // never inferred: absent means an ordinary, unlisted slot.
      finder_listed?: boolean
      finder_player_type_id?: string | null
    }) => createBalanceSlots(teamId, rosterId, body),
    onSuccess: invalidateWithPlayerFinder,
    onError: refetchOnConflict,
    retry: 0,
  })

  /**
   * SN-4709 × SN-8081 (D46) — tick / untick one open slot's Player Finder
   * listing. Same invalidate-and-refetch shape as every other mutation: the
   * server returns the refreshed sheet, so `finder_listed` and `finder_position`
   * come back from one source of truth rather than an optimistic guess.
   *
   * Listing requires an `action` (TF-02): "List" and "Edit position" both list
   * the slot, and only the caller knows which one the rep chose — a position
   * edit must never become consent to advertise.
   */
  const updateSlotFinderListingMutation = useMutation({
    mutationFn: (variables: SlotFinderListingVariables) =>
      updateSlotFinderListing(
        teamId,
        rosterId,
        variables.slotId,
        variables.listed
          ? {
              finder_listed: true,
              finder_action: variables.action,
              finder_player_type_id: variables.playerTypeId ?? null,
            }
          : { finder_listed: false, finder_player_type_id: null }
      ),
    onSuccess: invalidateWithPlayerFinder,
    onError: refetchOnConflict,
    retry: 0,
  })

  const updateSlotAmount = useMutation({
    mutationFn: ({
      slotId,
      amount,
      previewFingerprint,
      expectedKind,
    }: {
      slotId: string
      amount: number
      /** Sep 23 — an official line's change carries its preview's fingerprint. */
      previewFingerprint?: string
      expectedKind?: BalanceLineKind
    }) =>
      updateBalanceSlot(teamId, rosterId, slotId, {
        amount,
        ...(previewFingerprint
          ? { preview_fingerprint: previewFingerprint }
          : {}),
        ...(expectedKind ? { expected_kind: expectedKind } : {}),
      }),
    onSuccess: invalidate,
    onError: refetchWhenStale,
    retry: 0,
  })

  /**
   * Sep 23 — open a team official's budget line from their $0 candidate row, with the
   * fingerprint of the preview the rep confirmed. retry: 0 like every sibling: it writes
   * a line and moves money.
   *
   * Refetches after ANY failure, not only a 409: who may have a line changes outside
   * this tab (roles, invitations), and a reply that never arrived may still have opened
   * the line — the sheet must show what is true before the rep tries again.
   */
  const createOfficialLine = useMutation({
    mutationFn: (variables: {
      registrationId: string
      payerUserId: string
      teamOfficialUid: string | null
      amount: number
      previewFingerprint: string
    }) =>
      createBalanceOfficialLine(teamId, rosterId, {
        team_registration_id: variables.registrationId,
        payer_user_id: variables.payerUserId,
        team_official_id: variables.teamOfficialUid,
        amount: variables.amount,
        preview_fingerprint: variables.previewFingerprint,
      }),
    onSuccess: invalidate,
    onError: () => invalidate(),
    retry: 0,
  })

  // A2 — the rep's internal note. Same invalidate-and-refetch shape as every
  // other mutation (D20, no optimistic writes): the note comes back on the slot
  // in the refreshed payload, so there is one source of truth for what is stored.
  const updateSlotNoteMutation = useMutation({
    mutationFn: ({
      slotId,
      note,
      expectedPlayerRegistrationId,
    }: {
      slotId: string
      note: string | null
      expectedPlayerRegistrationId: string
    }) =>
      updateSlotNote(
        teamId,
        rosterId,
        slotId,
        note,
        expectedPlayerRegistrationId
      ),
    onSuccess: invalidate,
    onError: refetchOnConflict,
    retry: 0,
  })

  const removeSlot = useMutation({
    mutationFn: ({ slotId }: { slotId: string }) =>
      deleteBalanceSlot(teamId, rosterId, slotId),
    onSuccess: invalidateWithPlayerFinder,
    onError: refetchOnConflict,
    retry: 0,
  })

  const runAutoDistribute = useMutation({
    mutationFn: (body: { team_registration_id: string }) =>
      autoDistribute(teamId, rosterId, body),
    onSuccess: invalidate,
    onError: refetchOnConflict,
    retry: 0,
  })

  const updateSettings = useMutation({
    mutationFn: (body: {
      team_registration_id: string
      auto?: boolean
      locked?: boolean
      // SN-4709 Team Privacy — same contract. `invalidate` refetches the whole balance
      // tree, which is what puts the flipped value back on screen.
      is_private?: boolean
    }) => updateBalanceSettings(teamId, rosterId, body),
    // Private means unlisted, so a privacy flip either way also changes whether the
    // Player Finder shows the team's ad: the Player Finder settings refetch with the
    // sheet. `auto` and `locked` are money-only and keep the balance-only refetch.
    onSuccess: (_data, variables) =>
      variables.is_private !== undefined
        ? invalidateWithPlayerFinder()
        : invalidate(),
    onError: refetchOnConflict,
    retry: 0,
  })

  const lockSlot = useMutation({
    // F4: the preview fingerprint is REQUIRED — a lock is never attempted without the schedule
    // the admin just confirmed (the server rejects a request without it either way).
    mutationFn: ({
      slotId,
      previewFingerprint,
      expectedKind,
    }: {
      slotId: string
      previewFingerprint: string
      expectedKind?: BalanceLineKind
    }) =>
      lockBalanceSlot(
        teamId,
        rosterId,
        slotId,
        previewFingerprint,
        expectedKind
      ),
    onSuccess: invalidate,
    onError: refetchOnConflict,
    retry: 0,
  })

  /**
   * SN-4709 (James, Jul 31) — "Send N Invoices" from the sheet's row selection.
   * Each entry carries its own preview fingerprint, so the batch inherits the
   * per-slot anti-drift guarantee rather than trading it for convenience.
   *
   * retry: 0 like every sibling — this inserts billing rows and queues one
   * allocation email per player, so a re-send would double both.
   */
  const lockSlotsBatch = useMutation({
    mutationFn: (
      locks: {
        slot_id: string
        preview_fingerprint: string
        expected_kind?: BalanceLineKind
      }[]
    ) => lockBatch(teamId, rosterId, locks),
    onSuccess: invalidate,
    onError: refetchOnConflict,
    retry: 0,
  })

  const unlockSlot = useMutation({
    mutationFn: ({
      slotId,
      expectedKind,
    }: {
      slotId: string
      expectedKind?: BalanceLineKind
    }) => unlockBalanceSlot(teamId, rosterId, slotId, expectedKind),
    onSuccess: invalidate,
    onError: refetchOnConflict,
    retry: 0,
  })

  const releaseSlot = useMutation({
    mutationFn: ({ slotId }: { slotId: string }) =>
      releaseBalanceSlot(teamId, rosterId, slotId),
    onSuccess: invalidateWithPlayerFinder,
    onError: refetchOnConflict,
    retry: 0,
  })

  // SN-8310 review round 2 — adding or removing an invoice moves money, and a
  // reply that never arrived (a dropped connection, a 5xx from a proxy) leaves
  // the outcome unknown: the server may have written it. Refetch then, as after
  // a 409, so the sheet shows what happened instead of inviting a second add. A
  // stale preview wrote nothing but means the sheet moved (F3, above). Any other
  // 4xx is a refusal that wrote nothing, so the sheet is still true.
  const refetchUnlessRefused = (error: unknown) => {
    const status = (error as { status?: number } | null)?.status
    return status === undefined ||
      status === 409 ||
      status >= 500 ||
      isStalePreview(error)
      ? invalidate()
      : undefined
  }

  /**
   * SN-8310 Add Invoice — one more charge on an invoiced player's share. The body
   * carries the preview fingerprint, so the server refuses a charge that changed
   * since the rep saw it. retry: 0 like every sibling: this inserts a billing row
   * and queues the player's notice, so a re-send would double both.
   */
  const addInvoice = useMutation({
    mutationFn: ({
      slotId,
      body,
    }: {
      slotId: string
      body: AdditionalInvoiceBody
    }) => addAdditionalInvoice(teamId, rosterId, slotId, body),
    onSuccess: invalidate,
    onError: refetchUnlessRefused,
    retry: 0,
  })

  /**
   * SN-8310 — undo an unpaid added invoice; the share drops by its amount. On a team
   * official's line (Sep 23) the undo carries its removal preview's fingerprint.
   */
  const removeAddedInvoice = useMutation({
    mutationFn: ({
      slotId,
      billUid,
      expectedKind,
      previewFingerprint,
    }: {
      slotId: string
      billUid: string
      expectedKind?: BalanceLineKind
      previewFingerprint?: string
    }) =>
      removeAdditionalInvoice(teamId, rosterId, slotId, billUid, {
        expectedKind,
        previewFingerprint,
      }),
    onSuccess: invalidate,
    onError: refetchUnlessRefused,
    retry: 0,
  })

  // SN-4709 Phase 8: record / void offline payments. retry: 0 — the idempotency_key protects a
  // replay, but a client auto-retry could still race a slow first response, so never re-send.
  const recordOffline = useMutation({
    mutationFn: ({
      slotId,
      body,
    }: {
      slotId: string
      body: OfflinePaymentBody
    }) => recordOfflinePayment(teamId, rosterId, slotId, body),
    onSuccess: invalidate,
    retry: 0,
  })

  const voidOffline = useMutation({
    mutationFn: ({
      batchId,
      expectedKind,
    }: {
      batchId: string
      /** The batch's own `line_kind` (Sep 23). */
      expectedKind?: BalanceLineKind
    }) => voidOfflinePayment(teamId, rosterId, batchId, expectedKind),
    onSuccess: invalidate,
    retry: 0,
  })

  // D39: verification is what settles the money, so it is never auto-retried —
  // a retry could race a slow first response on a settling call.
  const verifyOffline = useMutation({
    mutationFn: ({
      batchId,
      settlementFingerprint,
      expectedKind,
    }: {
      batchId: string
      settlementFingerprint?: string
      /** The batch's own `line_kind` (Sep 23). */
      expectedKind?: BalanceLineKind
    }) =>
      verifyOfflinePayment(
        teamId,
        rosterId,
        batchId,
        settlementFingerprint,
        expectedKind
      ),
    onSuccess: invalidate,
    retry: 0,
  })

  // SN-4709 follow-up — balance COMMUNICATIONS. Deliberately NO onSuccess
  // invalidate: reminders/email/feed-post never change a balance, so refetching
  // the sheet (or discovery) would be a wasted round trip. retry: 0 for the same
  // non-idempotency reason as every mutation above — a queued send is never re-sent.
  const sendReminders = useMutation({
    mutationFn: (message?: string | null) =>
      sendPaymentReminders(teamId, rosterId, message),
    retry: 0,
  })

  const sendSlotReminderMutation = useMutation({
    mutationFn: ({
      slotId,
      message,
    }: {
      slotId: string
      message?: string | null
    }) => sendSlotReminder(teamId, rosterId, slotId, message),
    retry: 0,
  })

  const emailTeam = useMutation({
    mutationFn: (body: BalanceEmailBody) =>
      broadcastBalanceEmail(teamId, rosterId, body),
    retry: 0,
  })

  // The feed post is team-scoped; the roster only scopes the notify audience, so it
  // travels in the body rather than the URL.
  const postAndNotify = useMutation({
    mutationFn: ({ subject, message }: { subject: string; message: string }) =>
      postAndNotifyTeam(teamId, { subject, message, roster_uid: rosterId }),
    retry: 0,
  })

  return {
    createSlots,
    updateSlotAmount,
    createOfficialLine,
    updateSlotNote: updateSlotNoteMutation,
    updateSlotFinderListing: updateSlotFinderListingMutation,
    removeSlot,
    runAutoDistribute,
    updateSettings,
    lockSlot,
    lockSlotsBatch,
    unlockSlot,
    releaseSlot,
    addInvoice,
    removeAddedInvoice,
    recordOffline,
    voidOffline,
    verifyOffline,
    sendReminders,
    sendSlotReminder: sendSlotReminderMutation,
    emailTeam,
    postAndNotify,
  }
}

export default useTeamBalanceMutations
